import create from '../creator'
import axios from '@/axios-setup'
import router from '@/router'

import {
	CART_STATUS,
	CLASSIFICATION,
	ORDER_TYPES,
	PRODUCT_ORDER_TYPES
} from '@/constants'
import { checkCartId } from '@/utils/helpers'

const initialState = () => ({
	cartItems: [],
	fullItemsList: [],
	creditCardId: null,
	customerNameToPrint: '',
	email: '',
	hadSentAutoEmail: false,
	id: null,
	includeUtensils: false,
	// isPickUp and isInVenue will be removed from the 3.7
	isPickUp: false,
	isInVenue: false,
	location: '',
	notice: '',
	phoneNumber: '',
	pickupTimeFrom: null,
	pickupTimeTo: null,
	sentToTab: false,
	status: null,
	tab: {},
	tabId: null,
	tableId: null,
	tips: null,
	tipsPercent: null,
	totalCost: 0,
	totalItems: 0,
	useTipsPercent: false,
	venueId: null,
	orderType: 1,
	lastProductOrderType: 1,
	isCartRecreated: false,
	isTabFailed: false,
	cartInfoMessage: null,
	eventTicketId: null,
	isAsap: false
})
const state = initialState()

const actions = {
	async getCart({ commit, state, rootState }, isNeedShowExpiredPopup = false) {
		const venueId = state.venueId || rootState.venue.venueInfo.id

		if (!venueId) return

		const params = { venueId }

		const isDesktopDevice = rootState.ui.isDesktopDevice

		if (isDesktopDevice) {
			params.orderType = ORDER_TYPES.isPickUp
		}

		const resp = await axios.get(`cart`, { params })

		const isCheckPage = router.history?.current?.name === 'check'

		checkCartId(resp.data, isCheckPage ? true : isNeedShowExpiredPopup)

		commit('setCart', resp.data)

		return resp.data
	},

	async getCartByEventTicket({ commit, state, rootState }, eventTicketId) {
		const venueId = state.venueId || rootState.venue.venueInfo.id

		if (!venueId) return

		const params = { venueId }

		const resp = await axios.get(`/cart/getByEventTicket/${eventTicketId}`, {
			params
		})

		commit('setCart', resp.data)
	},

	async changeProductsType({ commit }, payload) {
		const { data } = await axios.post('cart/cartItems/setOrderType', payload)

		checkCartId(data)
		commit('setCart', data)
	},

	async removeProductsByType({ commit }, payload) {
		const { data } = await axios.post(
			'cart/cartItems/deleteByOrderType',
			payload
		)

		checkCartId(data)
		commit('setCart', data)
	},
	async updateCart({ commit, state }, data) {
		if (data.isAsap && !state.isAsap) {
			commit('setCart', {
				...state,
				isAsap: true,
				pickupTimeFrom: null,
				pickupTimeTo: null
			})
		} else if (data.needImmediateUpdate) {
			delete data.needImmediateUpdate

			commit('setCart', {
				...state,
				...data
			})
		}

		const { venueId } = state

		const isNeedShowExpiredPopup = Boolean(data?.isNeedShowExpiredPopup)

		delete data.isNeedShowExpiredPopup

		const payload = {
			...state,
			...data
		}

		const resp = await axios.put(`cart/updateByVenue/${venueId}`, payload)

		const isCheckPage = router.history?.current?.name === 'check'

		checkCartId(resp.data, isCheckPage ? true : isNeedShowExpiredPopup)

		commit('setCart', resp.data)

		return resp.data
	},

	async deleteCart({ commit }, cartId) {
		const resp = await axios.delete(`cart/${cartId}`)

		commit('deleteCart')

		return resp.data
	},

	async removeProducts(_, cartId) {
		// Returns 204 without data
		await axios.post(`cart/removeProducts/${cartId}`)
	},

	async reschedulePickUp({ commit }, { cartId, ...data }) {
		if (data.isAsap && !state.isAsap) {
			commit('setCart', {
				...state,
				isAsap: true,
				pickupTimeFrom: null,
				pickupTimeTo: null,
				tab: {
					...state.tab,
					isAsap: true
				}
			})
		}

		const resp = await axios.put(`cart/reschedulePickUp/${cartId}`, data)

		checkCartId(data, true)
		commit('setCart', data)

		return resp
	},

	async addCartItems({ commit }, data) {
		const { isNeedShowExpiredPopup } = data

		delete data.isNeedShowExpiredPopup

		const resp = await axios.post(`cart/addItem`, data)

		checkCartId(resp.data, isNeedShowExpiredPopup)
		commit('setCart', resp.data)
	},

	async updateCartItem({ commit }, { cartItemId, data }) {
		const resp = await axios.put(`cart/updateItem/${cartItemId}`, data)

		checkCartId(resp.data, true)
		commit('setCart', resp.data)
	},

	async deleteCartItem({ commit }, data) {
		const resp = await axios.post('cart/cartItems/deleteBySimilarityHash', data)

		const cartData = resp.data || initialState()

		commit('setCart', cartData)
	},

	async updateTab({ commit }, data) {
		const resp = await axios.post(`checkout/updateTab`, data)

		commit('setIsTabFailed', false)
		commit('setCart', resp.data)
	},

	async openTab({ commit }, data) {
		const resp = await axios.post(`checkout/createTab`, data)

		commit('setIsTabFailed', false)
		commit('setCart', resp.data)
	},

	async closeTab(_, { cartId }) {
		await axios.post(`checkout/cancelTab`, {
			cartId
		})
	},

	async cancelOrder({ commit }, { cartId }) {
		const resp = await axios.post(`checkout/closeOrder/${cartId}`)

		commit('setCart', resp.data)
	},

	setIsTabFailed({ commit }, isFailed) {
		commit('setIsTabFailed', isFailed)
	},

	async payOrder({ commit }, data) {
		const resp = await axios.post(`checkout/pay`, data)

		commit('payOrder')

		return resp.data
	},

	async sendPaymentReceipt(_, data) {
		const resp = await axios.post(`checkout/sendPaymentReceipt`, data)

		return resp.data
	},

	async setReceiptShown({ commit }, data) {
		if (!data) {
			return false
		}

		const resp = await axios.post(`cart/receiptViewed/${data}`)

		commit('setCart', resp.data)
	},

	setCartInfoMessage({ commit }, data) {
		commit('setCartInfoMessage', data)
	},

	setPaymentStatus({ commit }, status) {
		commit('setPaymentStatus', status)
	},

	toggleIsCartRecreated({ commit }) {
		commit('toggleIsCartRecreated')
	},

	clearCart({ commit }) {
		commit('clearCart')
	}
}

const mutations = {
	setCart(state, data) {
		const actualData = {
			...data,
			// isPickUp and isInVenue will be removed from the 3.6.1
			isPickUp: data.orderType === ORDER_TYPES.isPickUp,
			isInVenue: data.orderType === ORDER_TYPES.isInVenue,
			fullItemsList: data.cartItems,
			// Deleted products have to be removed from the list
			cartItems: data.cartItems.filter(item => !item.deleted)
		}

		Object.assign(state, actualData)
	},

	deleteCart(state) {
		Object.assign(state, initialState())
	},

	toggleIsCartRecreated(state) {
		state.isCartRecreated = !state.isCartRecreated
	},

	setIsTabFailed(state, isFailed) {
		state.isTabFailed = isFailed
	},

	setPaymentStatus(state, status) {
		state.status = status
	},

	setCartInfoMessage(state, payload) {
		state.cartInfoMessage = payload
	},

	payOrder() {},

	clearCart(state) {
		const { id } = state

		Object.assign(state, initialState())
		Object.assign(state, { id })
	}
}

const getters = {
	totalPrice(state) {
		return state.totalCost
	},

	isCartOrderTypeInVenue: state => state.orderType === ORDER_TYPES.isInVenue,

	isCartOrderTypePickUp: state => state.orderType === ORDER_TYPES.isPickUp,

	totalProductsCount(state) {
		return state.cartItems.reduce((acc, item) => {
			return acc + item.quantity
		}, 0)
	},

	isToGoProductsIncludesFood(_, getters) {
		return Boolean(
			getters.toGoProducts.some(
				product => product.classId === CLASSIFICATION.FOOD
			)
		)
	},

	isTabOpened(state) {
		return Boolean(state.tabId)
	},

	isTabStatusCreated() {
		return Boolean(state.status === CART_STATUS.TAB_CREATED && state.tabId)
	},

	isTabOpenedByRedemption(state, getters) {
		return Boolean(getters.isTabOpened && state.eventTicketId)
	},

	isTabEmpty(state, getters) {
		return getters.isTabOpened && !getters.orderItems.length
	},

	orderItems(state) {
		// isEightySixProduct for show and grouping 86 products on the check screen
		return state.fullItemsList.filter(
			item => item.tab?.pitId || item.isEightySixProduct
		)
	},

	cartItemsInOrder(state) {
		return state.cartItems.filter(item => item.tab?.pitId)
	},

	cartItemsBeyondOrder(state) {
		return state.cartItems.filter(item => !item.tab?.pitId)
	},

	isWaitingPos() {
		return state.status === CART_STATUS.WAITING_POS
	},

	isTabNotEditable(state, getters) {
		if (state.status === CART_STATUS.RECEIPT_VIEWED) return false

		return (
			state.sentToTab ||
			state.status === CART_STATUS.PAYMENT_SUCCESS ||
			state.status === CART_STATUS.CLOSED_AUTO_CLOSEOUT ||
			state.status === CART_STATUS.PAYMENT_IN_PROGRESS ||
			getters.isTabOpenedByRedemption
		)
	},

	openedTab(state) {
		return state.tab
	},

	cartHasFood(state) {
		return (
			state.cartItems &&
			state.cartItems.some(item => {
				return item.classId === CLASSIFICATION.FOOD
			})
		)
	},

	cartHasBeverage(state) {
		return (
			state.cartItems &&
			state.cartItems.some(item => {
				return item.classId === CLASSIFICATION.BEVERAGE
			})
		)
	},

	sumForTips(state) {
		const { sumForTips, amount } = state.tab
		return sumForTips || amount
	},

	toGoProducts(state) {
		return (
			state.cartItems?.filter(
				item => item.orderType === PRODUCT_ORDER_TYPES.toGo
			) || []
		)
	},

	cartTips(state) {
		return state.tips
	},

	cartTipsPercent(state) {
		return state.tipsPercent
	}
}

export default create(
	{
		namespaced: true,
		state,
		actions,
		mutations,
		getters
	},
	initialState()
)
